"use client";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Link from "next/link";
import { Spinner } from "@/components/ui/spinner";
import { ChevronLeft } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { useAuth } from "@/hooks/use-auth";
import { authRoutes } from "@/lib/constants";

type SignupFormProps = {
  openLoginModal?: () => void;
  onBackToAuthSelection?: () => void;
  isModal?: boolean;
};

/**
 * Signup Form Component
 *
 * Single Responsibility: Handle signup form UI and user interactions
 * Separation of Concerns: UI logic separated from auth and redirect logic
 * DRY: Reuses centralized auth logic
 */
export function SignupForm({
  openLoginModal,
  onBackToAuthSelection,
  isModal = false,
}: SignupFormProps) {
  const t = useTranslations("SignupForm");
  const locale = useLocale();

  // Form state (UI concern only)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");

  // Authentication (delegated to consolidated hook)
  const { state, actions } = useAuth();

  // Google authentication (unified implementation)

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await actions.signupWithCredentials({
      email,
      password,
      confirmPassword,
      fullName,
    });
  };

  return (
    <div
      className={`flex h-auto w-full flex-col items-center justify-center ${isModal ? "p-0" : "px-4 py-6 sm:px-6 sm:py-8 md:py-12"}`}
    >
      <Card
        className={`w-full ${isModal ? "max-w-full flex-shrink-0 rounded-none border-0 shadow-none" : "mx-auto max-w-[95%] shadow-lg sm:max-w-[420px] md:max-w-md"}`}
      >
        <CardHeader
          className={`space-y-1 text-center ${isModal ? "p-3 sm:p-4" : "p-4 sm:p-6"}`}
        >
          {onBackToAuthSelection && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackToAuthSelection}
              className={`absolute ${isModal ? "left-1 top-1" : "left-2 top-2 sm:left-4 sm:top-4"}`}
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="sr-only sm:not-sr-only">Back</span>
            </Button>
          )}
          <CardTitle
            className={`${isModal ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"} font-bold`}
          >
            {t("title")}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            {t("description")}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent
            className={`grid gap-2 sm:gap-3 ${isModal ? "px-3 py-2 sm:px-4 sm:py-3" : "p-4 sm:p-6"}`}
          >
            <div className="grid gap-1 sm:gap-2">
              <Label htmlFor="fullName" className="text-sm sm:text-base">
                {t("fullNameLabel")}
              </Label>
              <Input
                id="fullName"
                type="text"
                placeholder={t("fullNamePlaceholder")}
                autoComplete="name"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                disabled={state.isLoading}
                className="h-9 sm:h-10"
              />
            </div>
            <div className="grid gap-1 sm:gap-2">
              <Label htmlFor="email" className="text-sm sm:text-base">
                {t("emailLabel")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t("emailPlaceholder")}
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={state.isLoading}
                className="h-9 sm:h-10"
              />
            </div>

            {/* Public Organization Assignment Notice */}
            {email && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-800 sm:text-sm">
                  {t("publicOrgNotice")}
                </p>
              </div>
            )}

            <div className="grid gap-1 sm:gap-2">
              <Label htmlFor="password" className="text-sm sm:text-base">
                {t("passwordLabel")}
              </Label>
              <PasswordInput
                id="password"
                autoCompleteType="new-password"
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={state.isLoading}
                className="h-9 sm:h-10"
              />
            </div>
            <div className="grid gap-1 sm:gap-2">
              <Label htmlFor="confirmPassword" className="text-sm sm:text-base">
                {t("confirmPasswordLabel")}
              </Label>
              <PasswordInput
                id="confirmPassword"
                autoCompleteType="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                disabled={state.isLoading}
                className="h-9 sm:h-10"
              />
            </div>
            {state.error && (
              <p className="mt-1 break-words text-xs text-destructive sm:text-sm">
                {state.error}
              </p>
            )}

            {/* Privacy Policy and Terms Consent */}
            <div className="mt-2 text-center text-xs text-muted-foreground sm:text-sm">
              {t("consentText")}{" "}
              <Link
                href={`/${locale}/conditions-utilisation`}
                className="text-primary underline-offset-4 hover:underline"
                target="_blank"
              >
                {t("termsLink")}
              </Link>{" "}
              {t("andText")}{" "}
              <Link
                href={`/${locale}/politique-confidentialite`}
                className="text-primary underline-offset-4 hover:underline"
                target="_blank"
              >
                {t("privacyLink")}
              </Link>
              .
            </div>
          </CardContent>
          <CardFooter
            className={`flex flex-col gap-3 ${isModal ? "px-3 py-2 sm:px-4 sm:py-3" : "p-4 sm:p-6"}`}
          >
            <Button
              type="submit"
              className="h-9 w-full sm:h-10"
              disabled={state.isLoading}
            >
              {state.isLoading ? (
                <Spinner size="sm" className="mr-2 h-4 w-4" />
              ) : (
                t("createAccountButton")
              )}
            </Button>

            <div className="mt-1 text-center text-xs text-muted-foreground sm:text-sm">
              {t("loginPrompt")}{" "}
              {openLoginModal ? (
                <button
                  type="button"
                  onClick={openLoginModal}
                  className="underline hover:text-primary"
                >
                  {t("loginLink")}
                </button>
              ) : (
                <Link
                  href={authRoutes.MODAL_LOGIN}
                  passHref
                  className="underline hover:text-primary"
                >
                  {t("loginLink")}
                </Link>
              )}
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
